import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { Strain, StrainInput } from '@shared';
import { StrainForm } from '../components/StrainForm';
import { getProductRepository } from '../lib/productRepository';

/** Edit an existing strain, reusing the shared strain form. */
export function EditStrain(): JSX.Element {
  const { id } = useParams();
  const navigate = useNavigate();
  const [strain, setStrain] = useState<Strain | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    getProductRepository()
      .get(id)
      .then(setStrain)
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : 'Could not load strain.'),
      )
      .finally(() => setLoading(false));
  }, [id]);

  const handleSave = async (input: StrainInput): Promise<void> => {
    if (!id) return;
    setBusy(true);
    try {
      await getProductRepository().update(id, input);
      navigate(`/strains/${id}`);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <p className="muted">Loading…</p>;
  if (error)
    return (
      <div className="notice" role="alert">
        {error}
      </div>
    );
  if (!strain) return <p className="muted">Not found.</p>;

  return (
    <div className="stack">
      <h1 className="mt-0">Edit strain</h1>
      <div className="card">
        <StrainForm
          initial={strain}
          submitLabel="Save changes"
          onSubmit={handleSave}
          busy={busy}
        />
      </div>
    </div>
  );
}
